<template>
  <div class="layout__content">
    <div v-if="!isLoading && hasError" class="layout__screen error">
      <ErrorComponent :message="dv.errorData.message" :url="dv.errorData.url" />
    </div>
    <template v-if="!isLoading && !hasError">
      <Teleport to="#top-menu-bar" defer>
        <PageTopBar v-if="isReady">
          <template #item-name>
            <slot name="title">
              <span>{{ dv.recordName }}</span>
            </slot>
          </template>
          <template #buttons>
            <slot name="buttons">
              <template v-if="!dv.isEditing">
                <!-- Edit (leftmost, always has label) -->
                <BaseButton
                  v-if="canEdit"
                  :disabled="dv.isDeleted"
                  compact="auto"
                  :icon="['fa-solid', 'fa-edit']"
                  size="small"
                  variant="primary"
                  outlined
                  label="Edit"
                  @click="dv.toggleEditMode"
                />

                <!-- Domain buttons with labels -->
                <slot name="pre-buttons" />
                <slot name="action-buttons" />

                <!-- Icon-only utility buttons -->
                <BaseButton
                  compact="auto"
                  :icon="['fa-solid', 'fa-link']"
                  size="small"
                  variant="primary"
                  outlined
                  tooltip="Copy URL"
                  class="js-button-copy-link"
                  @click="dv.handleCopyLink"
                />

                <!-- Delete / Restore bar buttons (wide screens) -->
                <slot name="delete-buttons">
                  <template v-if="dv.canDelete && !disableDelete">
                    <DropdownButton
                      v-if="!dv.isDeleted"
                      class="cq-hide-narrow"
                      :icon="['fa-solid', 'fa-trash']"
                      size="small"
                      variant="danger"
                      outlined
                      :show-arrow="false"
                      :loading="dv.isDeleting"
                      :disabled="dv.isDeleting"
                      compact="auto"
                      align="end"
                      content-class="dd-content--danger dd-content--confirm"
                    >
                      <div class="dd-confirm-body">
                        <p class="mb-0">Delete {{ dv.recordName }}?</p>
                        <button
                          class="button is-danger is-small has-text-weight-bold dd-confirm-btn"
                          :class="{ 'is-loading': dv.isDeleting }"
                          :disabled="dv.isDeleting"
                          @click="dv.deleteRecord"
                        >
                          <span class="icon">
                            <i class="fa-solid fa-trash" />
                          </span>
                          <span>Delete</span>
                        </button>
                      </div>
                    </DropdownButton>
                    <BaseButton
                      v-else-if="dv.isDeleted"
                      class="cq-hide-narrow"
                      :icon="['fa-solid', 'fa-trash-restore']"
                      size="small"
                      variant="danger"
                      outlined
                      :loading="dv.isRestoring"
                      :disabled="dv.isRestoring"
                      compact="auto"
                      label="Restore"
                      @click="dv.restoreRecord"
                    />
                  </template>
                </slot>

                <!-- Overflow / actions menu — hide-when-empty suppresses the
                     button entirely when the dropdown has no visible items -->
                <DropdownButton
                  :key="`overflow-${isPageNarrow}`"
                  ref="overflowRef"
                  icon="fa-solid fa-ellipsis-v"
                  size="small"
                  variant="primary"
                  outlined
                  :show-arrow="false"
                  hide-when-empty
                  align="end"
                  @close="handleOverflowClose"
                >
                  <slot name="overflow-menu" :is-narrow="isPageNarrow" />
                  <!-- Delete in dropdown (narrow screens) -->
                  <slot name="overflow-delete" :is-narrow="isPageNarrow">
                    <div
                      v-if="isPageNarrow && dv.canDelete && !disableDelete"
                      class="dd-section dd-section--danger"
                    >
                      <template v-if="!dv.isDeleted">
                        <div
                          v-if="!confirmingDelete"
                          class="dd-item dd-item--danger"
                          @click="handleDeleteClick"
                        >
                          <span class="icon">
                            <i class="fa-solid fa-trash" />
                          </span>
                          Delete
                        </div>
                        <div v-else class="dd-confirm" @click.stop>
                          <p class="dd-confirm__text">
                            Delete {{ dv.recordName }}?
                          </p>
                          <button
                            class="button is-danger is-small is-fullwidth has-text-weight-bold"
                            :class="{ 'is-loading': dv.isDeleting }"
                            :disabled="dv.isDeleting"
                            @click="handleDeleteConfirm"
                          >
                            Confirm Delete
                          </button>
                        </div>
                      </template>
                      <a
                        v-else
                        class="dd-item dd-item--danger"
                        :class="{ 'is-loading': dv.isRestoring }"
                        @click="dv.restoreRecord"
                      >
                        <span class="icon">
                          <i class="fa-solid fa-trash-restore" />
                        </span>
                        Restore
                      </a>
                    </div>
                  </slot>
                </DropdownButton>
              </template>

              <!-- Edit mode: Cancel + extra edit buttons + Save & exit -->
              <template v-else-if="canEdit && dv.isEditing">
                <BaseButton
                  compact="never"
                  :icon="['fa-solid', 'fa-times']"
                  size="small"
                  outlined
                  :variant="dv.hasEdits ? 'danger' : 'primary'"
                  :label="dv.hasEdits ? 'Discard changes' : 'Close'"
                  @click="handleCancelClick"
                />
                <slot name="edit-buttons" />
                <BaseButton
                  :disabled="dv.hasEdits && dv.hasValidationErrors"
                  compact="never"
                  :icon="['fa-solid', 'fa-save']"
                  size="small"
                  variant="primary"
                  :outlined="!dv.canSaveEdits"
                  label="Save & exit"
                  @click="handleSave"
                />
              </template>
            </slot>
          </template>
          <template #notifications>
            <div
              v-if="dv.isDeleted"
              class="notification is-danger is-light is-size-7 p-2 m-0"
            >
              <span class="icon">
                <i class="fa-solid fa-exclamation-circle" />
              </span>
              <span class="has-text-weight-semibold">
                This item has been deleted
              </span>
            </div>
            <div
              v-if="dv.hasValidationErrors && dv.isEditing"
              class="notification is-danger is-light is-size-7 p-2 m-0"
            >
              <span class="has-text-weight-semibold">
                Please fix all validation errors before saving
              </span>
            </div>
            <div
              v-if="dv.hasEdits && !dv.isEditing"
              class="notification is-warning is-light is-size-7 p-2 m-0"
            >
              <span class="has-text-weight-semibold">
                You have unsaved changes
              </span>
            </div>
            <slot name="extra-notifications" />
          </template>
        </PageTopBar>
      </Teleport>
      <!-- content -->
      <div v-if="isReady" class="shell-content">
        <slot />
      </div>
      <NavGuard
        :nav-next="dv.nextRoute"
        :disabled="!dv.hasEdits && !hasExtraChanges"
      >
        <template #body>
          <slot name="nav-guard-body">
            <p>
              This page is asking you to confirm that you want to leave —
              information you've entered may not be saved.
            </p>
          </slot>
        </template>
        <template #footer="{ onDone, navNext }">
          <slot name="nav-guard-footer" v-bind="{ onDone, navNext }">
            <button
              class="button is-small is-danger"
              @click="
                () => {
                  dv.discardEdits()
                  onDone()
                  navNext()
                }
              "
            >
              <span class="icon"><i class="fa-solid fa-trash" /></span>
              <span>Discard</span>
            </button>
            <div>
              <button
                class="button is-primary is-small is-outlined"
                @click="onDone"
              >
                <span class="icon"><i class="fa-solid fa-redo" /></span>
                <span>Continue editing</span>
              </button>
              <button
                v-if="dv.canSaveEdits"
                class="button is-small is-primary"
                @click="
                  () => {
                    dv.saveEdits().then(() => {
                      onDone()
                      navNext()
                    })
                  }
                "
              >
                <span class="icon"><i class="fa-solid fa-save" /></span>
                <span>Save</span>
              </button>
            </div>
          </slot>
        </template>
      </NavGuard>
      <NavGuard v-model:open="showDiscardModal" title="Unsaved changes">
        <template #footer="{ onDone }">
          <button
            class="button is-danger is-small"
            @click="
              () => {
                handleDiscardConfirm()
                onDone()
              }
            "
          >
            <span class="icon"><i class="fa-solid fa-trash" /></span>
            <span>Discard</span>
          </button>
          <div style="display: flex; gap: var(--space-2)">
            <button
              class="button is-primary is-small is-outlined"
              @click="onDone"
            >
              <span class="icon"><i class="fa-solid fa-redo" /></span>
              <span>Continue editing</span>
            </button>
            <button
              class="button is-primary is-small"
              :disabled="!dv.canSaveEdits"
              @click="
                () => {
                  handleSaveFromDiscard()
                  onDone()
                }
              "
            >
              <span class="icon"><i class="fa-solid fa-save" /></span>
              <span>Save</span>
            </button>
          </div>
        </template>
      </NavGuard>
      <slot name="modals" />
    </template>
  </div>
</template>

<script>
import { computed, onBeforeUnmount, onMounted, provide, ref, toRef } from 'vue'

import { usePageShell } from '@/composables/usePageShell'

import ErrorComponent from '@/components/ErrorComponent.vue'
import NavGuard from '@/components/NavGuard.vue'
import PageTopBar from '@/components/PageTopBar.vue'

export default {
  name: 'DetailViewShell',
  components: {
    ErrorComponent,
    NavGuard,
    PageTopBar,
  },
  props: {
    dv: { type: Object, required: true },
    pageTitle: { type: String, default: '' },
    editable: { type: Boolean, default: true },
    disableDelete: { type: Boolean, default: false },
    beforeSave: { type: Function, default: null },
    hasExtraChanges: { type: Boolean, default: false },
  },
  setup(props) {
    provide('fieldPage', 'detail')

    // Track page-main width to toggle overflow-menu item visibility.
    // Mirrors the $cq-page-wide (860px) container-query breakpoint.
    const isPageNarrow = ref(false)
    let ro
    onMounted(() => {
      const main = document.querySelector('main')
      if (main) {
        ro = new ResizeObserver(([entry]) => {
          isPageNarrow.value = entry.contentRect.width <= 860
        })
        ro.observe(main)
      }
    })
    onBeforeUnmount(() => ro?.disconnect())

    const { dv } = props

    const isLoading = computed(() => !dv.isMetaReady || dv.isLoadingRecord)
    const showProgress = computed(() => isLoading.value || dv.isSaving)
    usePageShell({ title: toRef(props, 'pageTitle'), loading: showProgress })
    const isReady = computed(() => dv.isMetaReady && dv.isRecordLoaded)
    const hasError = computed(
      () => dv.errorData && Object.keys(dv.errorData).length > 0,
    )
    const canEdit = computed(() => props.editable !== false && dv.canEdit)

    const confirmingDelete = ref(false)
    const overflowRef = ref(null)

    function handleDeleteClick() {
      confirmingDelete.value = true
    }
    function handleDeleteConfirm() {
      dv.deleteRecord()
      confirmingDelete.value = false
    }
    function handleOverflowClose() {
      confirmingDelete.value = false
    }

    async function performSave() {
      if (props.beforeSave) await props.beforeSave()
      await dv.saveAndExit()
    }

    async function handleSave() {
      if (dv.hasEdits) {
        await performSave()
      } else {
        dv.exitEdit()
      }
    }

    const showDiscardModal = ref(false)

    function handleCancelClick() {
      if (dv.hasEdits) {
        showDiscardModal.value = true
      } else {
        dv.discardAndExit()
      }
    }

    function handleDiscardConfirm() {
      showDiscardModal.value = false
      dv.discardAndExit()
    }

    async function handleSaveFromDiscard() {
      showDiscardModal.value = false
      await performSave()
    }

    return {
      isPageNarrow,
      isLoading,
      isReady,
      hasError,
      canEdit,
      handleSave,
      confirmingDelete,
      overflowRef,
      handleDeleteClick,
      handleDeleteConfirm,
      handleOverflowClose,
      showDiscardModal,
      handleCancelClick,
      handleDiscardConfirm,
      handleSaveFromDiscard,
    }
  },
}
</script>

<style scoped>
.dd-confirm {
  padding: 0.5rem 0.75rem;
}

.dd-confirm__text {
  font-size: 0.85rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
}

.layout__content {
  display: contents;
}

.layout__screen {
  display: flex;
  flex-grow: 1;
  height: 100%;
}

.layout__screen.error {
  justify-content: center;
}
</style>
